// Tutorial tips (IMPLEMENTATION §2.4 phase 7 "tutorial actions", UX §4 hint rules; the UI is M7). The tips
// are an ordered list in defs.tutorial; `progress.tutorial` is the index of the current one (= tips done or
// skipped). A tip is ACTIVE (shown) while it is current, version-active and its `after` unlock has fired.
// A tip is SATISFIED when its `after` unlock has fired AND its own `done` predicate holds. Each tick the
// tutorial moves past the furthest satisfied tip (from the current one on): so a tip retires on its own
// condition, and a later tip retires the earlier ones only once the game has reached that later tip's phase
// (its unlock fired) and the player has done it — out-of-order play completes steps silently (UX §4). A
// predicate met before its tip's phase (e.g. hold-mining right after u01 meets holdToDig's `holdHits`) never
// skips the tips in between: it only lets that tip retire silently once its `after` fires (M4a review).
// Events: `hint` with `done: false` when a tip becomes active, `done: true` when the active tip retires
// (never for a tip that was skipped without being shown).
import type { Int } from './core/types.ts';
import type { Ctx } from './ctx.ts';
import { isActive, type SimDefs, type TutorialDef } from './defs.ts';
import type { TipId } from './gen/ids.ts';
import { triggerHolds } from './rules/triggers.ts';
import type { SimState } from './state.ts';
import { unlockIndex } from './unlocks.ts';

/** The tip the UI should show now, or null. */
export function activeTip(s: SimState, d: SimDefs): TutorialDef | null {
  const t = d.tutorial[s.progress.tutorial];
  if (t === undefined || !isActive(d, t.version)) return null;
  return s.progress.unlocked.includes(t.after) ? t : null;
}

/** A tip is satisfied once its phase has begun (`after` fired) and its own `done` predicate holds. */
export function tipSatisfied(s: SimState, d: SimDefs, t: TutorialDef): boolean {
  return (
    isActive(d, t.version) && s.progress.unlocked.includes(t.after) && triggerHolds(s, d, t.done)
  );
}

/** Index one past the furthest satisfied tip (from the current one on); unchanged if none. */
export function tutorialReach(s: SimState, d: SimDefs): Int {
  for (let j = d.tutorial.length - 1; j >= s.progress.tutorial; j--) {
    if (tipSatisfied(s, d, d.tutorial[j] as TutorialDef)) return j + 1;
  }
  return s.progress.tutorial;
}

/**
 * Advances the tutorial. `shownBefore` is the tip that was active before this phase-7 pass (the unlocks
 * that just fired may have made a new one active).
 */
export function runTutorial(c: Ctx, shownBefore: TipId | null): void {
  const reach = tutorialReach(c.s, c.d);
  if (reach > c.s.progress.tutorial) {
    if (shownBefore !== null) c.ev.push({ e: 'hint', id: shownBefore, done: true });
    c.s.progress.tutorial = reach;
    shownBefore = null;
  }
  const now = activeTip(c.s, c.d);
  if (now !== null && now.id !== shownBefore) c.ev.push({ e: 'hint', id: now.id, done: false });
}

/**
 * Debug goal jump to unlock index `a`: retires (silently) every tip whose phase lies before that unlock, so
 * the tip of the jumped-to phase (if any) is the one shown; a `hint` event announces it.
 */
export function skipTipsBefore(c: Ctx, a: Int): void {
  const before = activeTip(c.s, c.d);
  let j = c.s.progress.tutorial;
  while (j < c.d.tutorial.length && unlockIndex(c.d, (c.d.tutorial[j] as TutorialDef).after) < a)
    j++;
  if (j <= c.s.progress.tutorial) return;
  c.s.progress.tutorial = j;
  if (before !== null) c.ev.push({ e: 'hint', id: before.id, done: true }); // the shown tip retires
  const now = activeTip(c.s, c.d);
  if (now !== null) c.ev.push({ e: 'hint', id: now.id, done: false });
}
